import { useEffect, useMemo, useState } from "react";
import { Sparkles, GraduationCap, ChevronUp, ChevronDown, Users, Target, CalendarCheck, AlertCircle, Trophy } from "@/lib/icons";
import { useAuth } from "@/context/auth-context";

import defaultHeroBanner from "/it_banner.png";

const TINTO = {
    50: "#fdf2f4",
    100: "#fce7eb",
    200: "#f9cdd6",
    300: "#f4a3b5",
    400: "#ed6d8a",
    500: "#d94468",
    600: "#b82d53",
    700: "#7a1b33",
    800: "#6b1829",
    900: "#5a1523",
    950: "#320a12",
};

export function HeroBanner({ stats, onSinAtenderClick }) {
    const { user } = useAuth();
    const [isExpanded, setIsExpanded] = useState(true);

    useEffect(() => {
        if (window.innerWidth < 1024) {
            setIsExpanded(false);
        }
    }, []);

    const greeting = useMemo(() => {
        if (!user) return "";
        const feminineNames = ["Lulu", "Gaby"];
        const firstName = user.nombre ? user.nombre.split(" ")[0] : "Usuario";
        return feminineNames.includes(firstName) ? "Bienvenida" : "Bienvenido";
    }, [user]);

    const departmentLabel = user?.campus ?? "tu equipo";

    const quickStats = useMemo(
        () => [
            { label: "Total Leads", value: stats?.total ?? 0, icon: Users, color: TINTO[300], bg: "rgba(244,163,181,0.12)" },
            { label: "Citas Hoy", value: stats?.citasHoy ?? 0, icon: CalendarCheck, color: "#fbbf24", bg: "rgba(251,191,36,0.12)", pulse: (stats?.citasHoy ?? 0) > 0 },
            { label: "Sin atender", value: stats?.sinAtender ?? 0, icon: AlertCircle, color: (stats?.sinAtender ?? 0) > 0 ? "#f97316" : "#94a3b8", bg: (stats?.sinAtender ?? 0) > 0 ? "rgba(249,115,22,0.12)" : "rgba(148,163,184,0.08)", action: "sinAtender" },
            { label: "Inscritos del mes", value: stats?.inscritos ?? 0, icon: Trophy, color: "#34d399", bg: "rgba(52,211,153,0.12)" },
            { label: "Conversion", value: stats?.conversion ?? "0%", icon: Target, color: "#fbbf24", bg: "rgba(251,191,36,0.12)" },
        ],
        [stats],
    );

    if (!user) return null;

    const heroBackground = user.bannerImage || defaultHeroBanner;
    const heroPortrait = user.heroImage || null;
    const firstName = user.nombre ? user.nombre.split(" ")[0] : "Usuario";

    return (
        <div className="mb-4 transition-all duration-500">
            {/* ── Banner principal ── */}
            <div
                className={`relative mx-auto w-full max-w-[1680px] overflow-hidden rounded-2xl sm:rounded-3xl shadow-xl transition-all duration-500 ease-in-out ${isExpanded ? "h-[180px] lg:h-[190px] xl:h-[200px]" : "h-14"}`}
                style={{ backgroundColor: TINTO[950] }}
            >
                {/* Fondo base tinto */}
                <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${TINTO[900]}, ${TINTO[950]}, ${TINTO[800]}40)` }} />

                {/* Imagen solo visible expandido */}
                <div className={`absolute inset-0 transition-opacity duration-500 ${isExpanded ? "opacity-100" : "opacity-0"}`}>
                    {heroBackground && (
                        <img
                            src={heroBackground}
                            alt="Banner background"
                            className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                            style={{ objectPosition: "right center" }}
                            loading="lazy"
                        />
                    )}
                    <div className="absolute inset-0" style={{ backgroundColor: `${TINTO[700]}35` }} />
                    <div className="absolute inset-0" style={{ background: `linear-gradient(to right, ${TINTO[950]}cc, ${TINTO[950]}60, transparent)` }} />
                    <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${TINTO[950]}80, transparent)` }} />
                </div>

                {/* Toolbar colapsado */}
                <div
                    className="absolute top-3 right-0 left-0 h-10 z-30 flex items-center justify-between px-4 sm:px-6 cursor-pointer hover:bg-white/5 transition-colors"
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    <div className={`flex items-center gap-3 transition-opacity duration-300 ${isExpanded ? "opacity-0 pointer-events-none" : "opacity-100"}`}>
                        <div className="flex h-8 w-8 items-center justify-center rounded-full backdrop-blur" style={{ backgroundColor: `${TINTO[700]}40` }}>
                            <GraduationCap className="h-4 w-4" style={{ color: TINTO[300] }} />
                        </div>
                        <h3 className="text-sm font-bold text-white">
                            Hola {firstName} <Sparkles className="inline relative -top-0.5 h-3 w-3" style={{ color: TINTO[300] }} />
                        </h3>
                    </div>
                    <button
                        type="button"
                        className="ml-auto flex h-8 w-8 items-center justify-center rounded-full text-white backdrop-blur hover:bg-white/20 transition-colors"
                        style={{ backgroundColor: `${TINTO[700]}30` }}
                    >
                        {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                </div>

                {/* Contenido expandido */}
                <div className={`relative grid h-full grid-cols-1 ${heroPortrait ? 'lg:grid-cols-[minmax(0,1fr),200px]' : ''} transition-opacity duration-300 ${isExpanded ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                    <div className="flex flex-col justify-center gap-2 px-5 sm:px-6 lg:px-8 py-3 sm:py-3 h-full">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                            {/* Avatar */}
                            <div className="relative mx-auto flex h-[40px] w-[40px] flex-shrink-0 items-center justify-center sm:mx-0 sm:h-[44px] sm:w-[44px]">
                                <div className="absolute -inset-3 rounded-full opacity-50 blur-xl" style={{ background: `linear-gradient(135deg, ${TINTO[500]}, ${TINTO[700]})` }} />
                                <div className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-full bg-white/15 p-2 shadow-xl backdrop-blur">
                                    <div className="absolute inset-[5px] rounded-full bg-black/30" />
                                    {heroPortrait ? (
                                        <img src={heroPortrait} alt="Avatar" className="relative h-full w-full rounded-full object-cover" />
                                    ) : (
                                        <GraduationCap className="relative h-6 w-6 text-white" />
                                    )}
                                </div>
                            </div>

                            <div className="space-y-1 text-white text-center sm:text-left mx-auto sm:mx-0 flex-1">
                                <div className="space-y-0">
                                    <span className="text-[8px] uppercase tracking-[0.35em] sm:text-[9px] font-bold" style={{ color: TINTO[300] }}>
                                        Alcanza tus metas
                                    </span>
                                    <h1 className="text-base font-black leading-tight drop-shadow-xl sm:text-lg lg:text-xl">
                                        {greeting}{" "}
                                        <span className="bg-gradient-to-r from-white via-slate-100 to-slate-300 bg-clip-text text-transparent">
                                            {firstName}
                                        </span>
                                    </h1>
                                </div>
                                <p className="max-w-xl text-xs leading-tight text-white/70 hidden lg:block">
                                    Impulsa tu rendimiento dando seguimiento a los prospectos de{" "}
                                    <span className="font-semibold text-white">{departmentLabel}</span>.
                                </p>

                                {/* Stats cards con blur DENTRO del banner */}
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-1.5 sm:gap-2 mt-2 lg:max-w-3xl">
                                    {quickStats.map(({ label, value, icon: Icon, color, pulse, action }) => {
                                        const clickable = action === "sinAtender" && onSinAtenderClick && (value > 0)
                                        const Tag = clickable ? "button" : "div"
                                        return (
                                            <Tag
                                                key={label}
                                                type={clickable ? "button" : undefined}
                                                onClick={clickable ? onSinAtenderClick : undefined}
                                                className={`group flex items-center gap-2 rounded-lg border border-white/20 bg-white/5 px-2 py-1.5 shadow-md backdrop-blur transition-transform hover:bg-white/10 ${pulse ? "animate-pulse" : ""} ${clickable ? "cursor-pointer hover:scale-[1.03] active:scale-95 ring-1 ring-orange-400/40 hover:ring-orange-300" : ""}`}
                                                title={clickable ? "Ver prospectos sin atender" : undefined}
                                            >
                                                <div className="hidden sm:flex h-6 w-6 items-center justify-center rounded-md bg-white/10 text-white flex-shrink-0">
                                                    <Icon className="h-3 w-3" style={{ color }} />
                                                </div>
                                                <div className="text-left min-w-0 leading-tight">
                                                    <div className="text-[8px] sm:text-[9px] font-semibold uppercase tracking-wider text-white/50 truncate">{label}</div>
                                                    <div className="text-xs sm:text-sm font-bold text-white tabular-nums">{value}</div>
                                                </div>
                                            </Tag>
                                        )
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Metricas debajo cuando esta colapsado ── */}
            <div className={`overflow-hidden transition-all duration-500 ease-in-out ${isExpanded ? "max-h-0 opacity-0" : "max-h-48 sm:max-h-24 opacity-100 mt-2.5"}`}>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-2.5">
                    {quickStats.map(({ label, value, icon: Icon, color, pulse, action }) => {
                        const clickable = action === "sinAtender" && onSinAtenderClick && (value > 0)
                        const Tag = clickable ? "button" : "div"
                        return (
                            <Tag
                                key={label}
                                type={clickable ? "button" : undefined}
                                onClick={clickable ? onSinAtenderClick : undefined}
                                className={`flex items-center gap-2 sm:gap-2.5 rounded-xl border px-3 sm:px-3.5 py-2.5 sm:py-3 shadow-md transition-all hover:shadow-lg hover:-translate-y-0.5 bg-gradient-to-br from-rose-50 to-white dark:from-slate-800/80 dark:to-slate-900/80 border-rose-700/20 dark:border-slate-700/60 ${pulse ? "ring-1 ring-amber-300/50" : ""} ${clickable ? "cursor-pointer text-left ring-1 ring-orange-400/50 hover:ring-orange-500" : ""}`}
                                title={clickable ? "Ver prospectos sin atender" : undefined}
                            >
                                <div className="flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-lg flex-shrink-0 shadow-sm" style={{ backgroundColor: `${color}22`, border: `1px solid ${color}33` }}>
                                    <Icon className="h-4 w-4 sm:h-4.5 sm:w-4.5" style={{ color }} />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-base sm:text-lg font-extrabold tabular-nums leading-none text-rose-800 dark:text-rose-200">{value}</p>
                                    <p className="text-[9px] sm:text-[10px] font-semibold uppercase tracking-wider truncate mt-0.5 text-rose-400 dark:text-rose-400/70">{label}</p>
                                </div>
                            </Tag>
                        )
                    })}
                </div>
            </div>
        </div>
    );
}
